import { FormProps } from 'antd';
import { ICreateReport, IDocumentDownload } from 'interfaces/declarations';
import { generateURLWithFilters } from 'services/certificate';
import { getAPI } from 'requests/api';
import { getReportDownload, getReportStatus } from 'services/report';
import { useCallback, useEffect, useRef, useState } from 'react';

interface INotification {
  open?: (params: {
    type: 'success' | 'error';
    message: string;
    description?: string;
  }) => void;
}

interface IUseCorporateReportExportProps<TFilters extends object> {
  storageKey: string;
  searchFormProps: FormProps;
  notification: INotification;
  reportPath?: string;
  buildExportFilters?: (formValues: TFilters) => object;
  createReport?: (formValues: TFilters) => Promise<{ id: string }>;
  downloadFile?: TFileDownloader;
  maxAgeMs?: number;
  showErrorLog?: boolean;
}

type TFileDownloader = (
  reportId: string,
  fileId: string
) => Promise<IDocumentDownload>;

interface IPollHandlers {
  finish: () => void;
  notifyError: (description?: string) => void;
  downloadFile?: TFileDownloader;
  // Relatório parado em fila/geração há mais que isto conta como falha. Sem prazo
  // quando ausente, como sempre foi para os relatórios que já existiam.
  maxAgeMs?: number;
  // Mostra o motivo gravado pelo atlas (`error_log`) quando o relatório falha.
  showErrorLog?: boolean;
}

const RUNNING_STATUSES = new Set(['pending', 'enqueued', 'processing']);

export const REPORT_TIMEOUT_MESSAGE =
  'O arquivo está demorando mais que o esperado. Tente de novo mais tarde ou fale com o suporte.';

const isOverdue = (report: ICreateReport, maxAgeMs?: number) =>
  maxAgeMs !== undefined &&
  RUNNING_STATUSES.has(report.status) &&
  Date.now() - new Date(report.created_at).getTime() > maxAgeMs;

// A rota pública não pede login: serve aos relatórios que já a usavam. Arquivo com
// dado pessoal passa `downloadFile` com a rota autenticada do admin.
const publicFileDownload: TFileDownloader = (_reportId, fileId) =>
  getReportDownload(fileId);

const POLL_INTERVAL_MS = 3000;
// A geração do relatório é assíncrona e pode levar minutos. Uma falha pontual de
// polling (rede/5xx momentâneo) não deve abortar o fluxo nem exibir "Erro no
// Download" — só desistimos após várias falhas consecutivas.
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

// Relatório pronto: baixamos o arquivo. Uma falha aqui é terminal (o relatório
// já foi gerado), diferente de uma falha transitória de polling de status.
const downloadReport = async (
  reportId: string,
  fileId: string,
  notifyError: () => void,
  downloadFile: TFileDownloader
) => {
  try {
    const documentUrl = await downloadFile(reportId, fileId);

    if (documentUrl.url) {
      window.open(documentUrl.url, '_self');
    } else {
      notifyError();
    }
  } catch {
    notifyError();
  }
};

// Executa uma verificação de status. Retorna o número de falhas consecutivas de
// polling: zera quando o status é obtido com sucesso e incrementa quando a
// consulta falha, só desistindo após MAX_CONSECUTIVE_POLL_FAILURES falhas.
export const runReportPoll = async (
  reportId: string,
  failures: number,
  {
    finish,
    notifyError,
    downloadFile = publicFileDownload,
    maxAgeMs,
    showErrorLog = false
  }: IPollHandlers
): Promise<number> => {
  try {
    const report = await getReportStatus(reportId);

    if (report.status === 'processed') {
      finish();
      await downloadReport(
        reportId,
        report.files?.[0]?.id || '',
        notifyError,
        downloadFile
      );
    } else if (report.status === 'error') {
      finish();
      notifyError((showErrorLog && report.error_log) || undefined);
    } else if (isOverdue(report, maxAgeMs)) {
      finish();
      notifyError(REPORT_TIMEOUT_MESSAGE);
    }

    return 0;
  } catch {
    const nextFailures = failures + 1;

    if (nextFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
      finish();
      notifyError();
    }

    return nextFailures;
  }
};

export const useCorporateReportExport = <TFilters extends object>({
  storageKey,
  searchFormProps,
  notification,
  reportPath,
  buildExportFilters,
  createReport,
  downloadFile,
  maxAgeMs,
  showErrorLog
}: IUseCorporateReportExportProps<TFilters>) => {
  const [reportId, setReportId] = useState(
    localStorage.getItem(storageKey) || ''
  );
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleSetReportId = useCallback(
    (id: string) => {
      setReportId(id);

      if (id) {
        localStorage.setItem(storageKey, id);
      } else {
        localStorage.removeItem(storageKey);
      }
    },
    [storageKey]
  );

  const notifyDownloadError = useCallback(
    (description?: string) => {
      notification.open?.({
        type: 'error',
        message: 'Erro no Download',
        description: description || 'Erro ao baixar o relatório.'
      });
    },
    [notification]
  );

  const handleExportReport = useCallback(async () => {
    try {
      const formValues = searchFormProps.form?.getFieldsValue() as TFilters;
      let newReportId: string;

      if (createReport) {
        const result = await createReport(formValues);
        newReportId = result.id;
      } else {
        const filters = buildExportFilters!(formValues);
        const response = await getAPI().get(
          generateURLWithFilters(filters, reportPath!)
        );
        newReportId = response.data.id;
      }

      handleSetReportId(newReportId);

      notification.open?.({
        type: 'success',
        message:
          'O relatório está sendo gerado, e você será notificado quando estiver pronto para download.'
      });
    } catch (error) {
      handleSetReportId('');
      notification.open?.({
        type: 'error',
        message: 'Erro no Download',
        // O interceptor do api.ts lança um objeto com `message`, não um Error.
        description:
          (error as { message?: string } | undefined)?.message ||
          'Erro ao baixar o relatório.'
      });
    }
  }, [
    buildExportFilters,
    createReport,
    handleSetReportId,
    notification,
    reportPath,
    searchFormProps.form
  ]);

  useEffect(() => {
    if (!reportId) {
      return;
    }

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const finish = () => {
      stopPolling();
      handleSetReportId('');
    };

    let failures = 0;

    const tick = async () => {
      failures = await runReportPoll(reportId, failures, {
        finish,
        notifyError: notifyDownloadError,
        downloadFile,
        maxAgeMs,
        showErrorLog
      });
    };

    stopPolling();
    intervalRef.current = setInterval(tick, POLL_INTERVAL_MS);

    return stopPolling;
  }, [
    reportId,
    handleSetReportId,
    notifyDownloadError,
    downloadFile,
    maxAgeMs,
    showErrorLog
  ]);

  // Para de esperar o relatório aqui; a geração no servidor segue até o fim.
  const cancel = useCallback(() => handleSetReportId(''), [handleSetReportId]);

  return {
    reportId,
    handleExportReport,
    cancel
  };
};
